/**
 * Chat-to-Pay — painel do piloto.
 *
 * Três separadores: os números (conversão, abandono no checkout,
 * escalonamento, tempo mediano até pagar, receita), as conversas com o
 * registo de eventos de cada uma, e os comerciantes do canal (que loja está
 * aberta, quem recebe, saudação, limite por conversa). O simulador permite
 * conversar com a máquina sem WhatsApp.
 *
 * Nunca mostra o contacto do cliente: a API não o devolve.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Grid2 as Grid, Paper, Tabs, Tab,
  TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Alert,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Switch, Tooltip, CircularProgress, Snackbar, Stack, IconButton,
} from '@mui/material';
import { Refresh, Forum, Storefront, QueryStats, Send, ContentCopy, Bolt, Add, DeleteOutline } from '@mui/icons-material';
import DataTable, { Column, DateCell } from '../../components/Common/DataTable';
import {
  chatToPayService, PainelChatToPay, SessaoChat, EventoChat, ComercianteChat, ServicoChat, EntidadeSisp,
} from '../../services/chat-to-pay.service';

const cve = (n: number | null | undefined) =>
  n == null ? '—' : Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ' CVE';
const pct = (n: number | null | undefined) => (n == null ? '—' : `${n}%`);

const COR_ESTADO: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'> = {
  SAUDACAO: 'default', CATALOGO: 'info', CARRINHO: 'primary', CONFIRMACAO: 'primary', PAGAMENTO: 'warning',
  POS_VENDA: 'success', HUMANO: 'error', ENCERRADA: 'default',
  ABERTO: 'info', CONFIRMADO: 'primary', LINK_GERADO: 'warning', PAGO: 'success', ABANDONADO: 'default', CANCELADO: 'default', EXPIRADO: 'default',
};
const EstadoChip: React.FC<{ estado: string | null }> = ({ estado }) =>
  estado ? <Chip size="small" label={estado} color={COR_ESTADO[estado] || 'default'} variant={COR_ESTADO[estado] === 'default' ? 'outlined' : 'filled'} /> : <>—</>;

const Kpi: React.FC<{ valor: string; rotulo: string; nota?: string; cor?: string }> = ({ valor, rotulo, nota, cor }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="h4" fontWeight="bold" color={cor || 'text.primary'} sx={{ fontVariantNumeric: 'tabular-nums' }}>{valor}</Typography>
      <Typography variant="body2" color="text.secondary">{rotulo}</Typography>
      {nota && <Typography variant="caption" color="text.secondary">{nota}</Typography>}
    </CardContent>
  </Card>
);

const ChatToPay: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [dias, setDias] = useState(7);
  const [canal, setCanal] = useState('');
  const [painel, setPainel] = useState<PainelChatToPay | null>(null);
  const [sessoes, setSessoes] = useState<SessaoChat[]>([]);
  const [comerciantes, setComerciantes] = useState<ComercianteChat[]>([]);
  const [servicos, setServicos] = useState<ServicoChat[]>([]);
  const [servico, setServico] = useState<ServicoChat | null>(null);
  const [formS, setFormS] = useState({ rotulo: '', ajuda_referencia: '', valor_min: '' as string | number, valor_max: '' as string | number, activo: true });
  const [novoAberto, setNovoAberto] = useState(false);
  const [entidades, setEntidades] = useState<EntidadeSisp[]>([]);
  const [formN, setFormN] = useState({ entidade_id: '', tipo_servico: 'RCG' as 'RCG' | 'FAT', rotulo: '', devolve_token: false });
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [msg, setMsg] = useState('');

  const [sessao, setSessao] = useState<SessaoChat | null>(null);
  const [eventos, setEventos] = useState<EventoChat[]>([]);
  const [comerciante, setComerciante] = useState<ComercianteChat | null>(null);
  const [form, setForm] = useState({ recebe_email: '', saudacao: '', limite_sessao_cve: '' as string | number, activo: true, numero_whatsapp: '', instancia: '' });

  // instância de WhatsApp da loja: QR e estado
  const [qrLoja, setQrLoja] = useState<{ lojaId: string; nome: string; instancia: string | null; estado: string; qr: string | null; numero?: string | null; erro?: string } | null>(null);
  const [qrOcupado, setQrOcupado] = useState(false);
  const verInstancia = async (lojaId: string, nome: string) => {
    setQrOcupado(true);
    try { const r = await chatToPayService.instancia(lojaId); setQrLoja({ lojaId, nome, ...r.data.data }); }
    catch (e: any) { setErro(e?.response?.data?.error || 'Não foi possível consultar a instância'); }
    finally { setQrOcupado(false); }
  };
  useEffect(() => {
    if (!qrLoja || qrLoja.estado === 'open' || qrLoja.estado === 'sem_instancia') return;
    const t = setInterval(async () => { try { const r = await chatToPayService.instancia(qrLoja.lojaId); setQrLoja((q) => q ? { ...q, ...r.data.data } : q); } catch { /* tenta outra vez */ } }, 5000);
    return () => clearInterval(t);
  }, [qrLoja]);

  const [simAberto, setSimAberto] = useState(false);
  const [simContacto] = useState(() => '23859' + String(Math.floor(Math.random() * 90000) + 10000));
  const [simTexto, setSimTexto] = useState('');
  const [simLinhas, setSimLinhas] = useState<Array<{ de: 'eu' | 'lyra' | 'nota'; texto: string }>>([]);
  const [simOcupado, setSimOcupado] = useState(false);

  const carregar = useCallback(async () => {
    try {
      setLoading(true); setErro(null);
      const params = { dias, ...(canal ? { canal } : {}) };
      const [p, s, c, sv] = await Promise.all([
        chatToPayService.painel(params),
        chatToPayService.sessoes({ limit: 100, ...(canal ? { canal } : {}) }),
        chatToPayService.comerciantes(),
        chatToPayService.servicos(),
      ]);
      setPainel(p.data); setSessoes(s.data.data); setComerciantes(c.data.data); setServicos(sv.data.data);
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Não foi possível carregar o painel');
    } finally { setLoading(false); }
  }, [dias, canal]);
  useEffect(() => { carregar(); }, [carregar]);

  const abrirSessao = async (s: SessaoChat) => {
    setSessao(s); setEventos([]);
    try { const r = await chatToPayService.eventos(s.id); setEventos(r.data.data); } catch { setEventos([]); }
  };

  const abrirComerciante = (c: ComercianteChat) => {
    setComerciante(c);
    setForm({ recebe_email: c.recebe_email || '', saudacao: c.saudacao || '', limite_sessao_cve: c.limite_sessao_cve ?? '', activo: c.no_canal ? c.activo : true, numero_whatsapp: c.numero_whatsapp || '', instancia: c.instancia || '' });
  };
  const guardarComerciante = async () => {
    if (!comerciante) return;
    try {
      await chatToPayService.guardarComerciante(comerciante.loja_id, {
        activo: form.activo, saudacao: form.saudacao || null,
        limite_sessao_cve: form.limite_sessao_cve === '' ? null : Number(form.limite_sessao_cve),
        numero_whatsapp: form.numero_whatsapp || null, instancia: form.instancia || null,
        ...(form.recebe_email ? { recebe_email: form.recebe_email } : {}),
      });
      const r = await chatToPayService.comerciantes(); // só para refrescar depois
      setMsg(`«${comerciante.nome}» guardada`); const nome = comerciante.nome; const lojaId = comerciante.loja_id; setComerciante(null); void r; carregar();
      if (form.numero_whatsapp) verInstancia(lojaId, nome);
    } catch (e: any) { setErro(e?.response?.data?.error || 'Não foi possível guardar'); }
  };
  const alternarActivo = async (c: ComercianteChat) => {
    if (!c.no_canal) { abrirComerciante(c); return; }
    try { await chatToPayService.guardarComerciante(c.loja_id, { activo: !c.activo }); setMsg(`«${c.nome}» ${!c.activo ? 'aberta' : 'fechada'} no canal`); carregar(); }
    catch (e: any) { setErro(e?.response?.data?.error || 'Não foi possível alterar'); }
  };

  const abrirNovo = async () => {
    try { const r = await chatToPayService.entidadesSisp(); setEntidades(r.data.data); setFormN({ entidade_id: '', tipo_servico: 'RCG', rotulo: '', devolve_token: false }); setNovoAberto(true); }
    catch (e: any) { setErro(e?.response?.data?.error || 'Não foi possível carregar as entidades'); }
  };
  const criarServico = async () => {
    if (!formN.entidade_id) return;
    try {
      await chatToPayService.criarServico({ entidade_id: formN.entidade_id, tipo_servico: formN.tipo_servico, rotulo: formN.rotulo || undefined, devolve_token: formN.devolve_token });
      setMsg('Serviço criado, fechado. Confere o rótulo e a ajuda antes de abrir.'); setNovoAberto(false); carregar();
    } catch (e: any) { setErro(e?.response?.data?.error || 'Não foi possível criar'); }
  };
  const removerServico = async (sv: ServicoChat) => {
    if (!window.confirm(`Remover «${sv.rotulo}»? Só é possível se ainda não tiver pedidos.`)) return;
    try { await chatToPayService.removerServico(sv.id); setMsg('Serviço removido'); carregar(); }
    catch (e: any) { setErro(e?.response?.data?.error || 'Não foi possível remover'); }
  };

  const abrirServico = (sv: ServicoChat) => {
    setServico(sv);
    setFormS({ rotulo: sv.rotulo, ajuda_referencia: sv.ajuda_referencia || '', valor_min: sv.valor_min ?? '', valor_max: sv.valor_max ?? '', activo: sv.activo });
  };
  const guardarServico = async () => {
    if (!servico) return;
    try {
      await chatToPayService.guardarServico(servico.id, {
        activo: formS.activo, rotulo: formS.rotulo,
        ajuda_referencia: formS.ajuda_referencia || null,
        valor_min: formS.valor_min === '' ? null : Number(formS.valor_min),
        valor_max: formS.valor_max === '' ? null : Number(formS.valor_max),
      });
      setMsg(`«${servico.rotulo}» guardado`); setServico(null); carregar();
    } catch (e: any) { setErro(e?.response?.data?.error || 'Não foi possível guardar'); }
  };
  const alternarServico = async (sv: ServicoChat) => {
    try { await chatToPayService.guardarServico(sv.id, { activo: !sv.activo }); setMsg(`«${sv.rotulo}» ${!sv.activo ? 'aberto' : 'fechado'}`); carregar(); }
    catch (e: any) { setErro(e?.response?.data?.error || 'Não foi possível alterar'); }
  };

  const simularEnviar = async () => {
    const t = simTexto.trim(); if (!t || simOcupado) return;
    setSimTexto(''); setSimLinhas((l) => [...l, { de: 'eu', texto: t }]); setSimOcupado(true);
    try {
      const r = await chatToPayService.simular({ contacto: simContacto, mensagem: t, nome: 'Simulador' });
      const d = r.data;
      const novas: Array<{ de: 'eu' | 'lyra' | 'nota'; texto: string }> = (d.respostas || []).map((x: string) => ({ de: 'lyra' as const, texto: x }));
      if (!novas.length) novas.push({ de: 'nota', texto: 'sem resposta (a máquina está calada neste estado)' });
      novas.push({ de: 'nota', texto: `${d.estadoAntes} → ${d.estadoDepois}${d.intencao ? ` · ${d.intencao.intencao} (${d.intencao.origem})` : ''}${d.carrinho ? ` · carrinho ${d.carrinho.estado} ${cve(d.carrinho.total)}` : ''}` });
      setSimLinhas((l) => [...l, ...novas]);
    } catch (e: any) {
      setSimLinhas((l) => [...l, { de: 'nota', texto: 'erro: ' + (e?.response?.data?.error || e.message) }]);
    } finally { setSimOcupado(false); }
  };

  const colunasSessoes: Column<SessaoChat>[] = [
    { id: 'created_at', label: 'Início', minWidth: 130, format: (v) => <DateCell value={v} /> },
    { id: 'canal', label: 'Canal', minWidth: 90, format: (v) => <Chip size="small" variant="outlined" label={v} /> },
    { id: 'loja', label: 'Loja', minWidth: 130, format: (v) => v || '—' },
    { id: 'nome_cliente', label: 'Cliente', minWidth: 110, format: (v) => v || '—' },
    { id: 'estado', label: 'Estado', minWidth: 110, format: (v) => <EstadoChip estado={v} /> },
    { id: 'carrinho_estado', label: 'Pedido', minWidth: 110, format: (v, r) => v ? <Stack direction="row" spacing={1} alignItems="center"><EstadoChip estado={v} /><span>{cve(r.carrinho_total)}</span></Stack> : '—' },
    { id: 'mensagens_recebidas', label: 'Msgs', minWidth: 70, align: 'right', format: (v, r) => `${v} / ${r.mensagens_enviadas}` },
    { id: 'ultima_msg_cliente_em', label: 'Última do cliente', minWidth: 130, format: (v) => v ? <DateCell value={v} /> : '—' },
  ];

  const ind = painel?.indicadores; const din = painel?.dinheiro; const car = painel?.carrinhos; const ses = painel?.sessoes; const men = painel?.mensagens;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Chat-to-Pay</Typography>
          <Typography variant="body2" color="text.secondary">Vender por WhatsApp: conversa, carrinho, link PagaMe, confirmação.</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField select size="small" label="Período" value={dias} onChange={(e) => setDias(Number(e.target.value))} sx={{ minWidth: 120 }}>
            <MenuItem value={1}>Hoje</MenuItem><MenuItem value={7}>7 dias</MenuItem><MenuItem value={30}>30 dias</MenuItem><MenuItem value={90}>90 dias</MenuItem>
          </TextField>
          <TextField select size="small" label="Canal" value={canal} onChange={(e) => setCanal(e.target.value)} sx={{ minWidth: 140 }}>
            <MenuItem value="">Todos</MenuItem><MenuItem value="WHATSAPP">WhatsApp</MenuItem><MenuItem value="SIMULADOR">Simulador</MenuItem>
          </TextField>
          <Button variant="outlined" startIcon={<Forum />} onClick={() => setSimAberto(true)}>Simular conversa</Button>
          <Button variant="outlined" startIcon={<Refresh />} onClick={carregar}>Actualizar</Button>
        </Box>
      </Box>

      {erro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>{erro}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}><Kpi valor={String(ses?.total ?? '—')} rotulo="Conversas" nota={ses ? `${ses.whatsapp} WhatsApp · ${ses.simulador} simulador` : undefined} /></Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}><Kpi valor={String(car?.pagos ?? '—')} rotulo="Pedidos pagos" nota={car ? `${car.links_gerados} links gerados` : undefined} cor="success.main" /></Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}><Kpi valor={cve(din?.receita_cve)} rotulo="Receita" nota={din?.ticket_medio_cve != null ? `ticket médio ${cve(din.ticket_medio_cve)}` : undefined} /></Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}><Kpi valor={pct(ind?.conversao_pct)} rotulo="Conversão" nota="conversas com carrinho que pagaram" cor="primary.main" /></Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}><Kpi valor={pct(ind?.abandono_checkout_pct)} rotulo="Abandono no checkout" nota="links gerados sem pagamento" cor={ind?.abandono_checkout_pct != null && ind.abandono_checkout_pct > 50 ? 'warning.main' : undefined} /></Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}><Kpi valor={ind?.tempo_mediano_min != null ? `${ind.tempo_mediano_min} min` : '—'} rotulo="Tempo mediano até pagar" nota={ind ? `escalonamento ${pct(ind.escalonamento_pct)}` : undefined} /></Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}><Kpi valor={String(painel?.servicos?.pagos ?? '—')} rotulo="Serviços pagos" nota={painel?.servicos ? `${cve(painel.servicos.receita_cve)} · ${painel.servicos.com_token} com código` : undefined} cor="secondary.main" /></Grid>
      </Grid>

      {men && (men.erros_envio > 0 || men.pan_redigidos > 0 || (car?.confirmacoes_falhadas ?? 0) > 0) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {men.erros_envio > 0 && <>Falhas de envio ao WhatsApp: <b>{men.erros_envio}</b>. </>}
          {(car?.confirmacoes_falhadas ?? 0) > 0 && <>Pagamentos sem confirmação enviada: <b>{car!.confirmacoes_falhadas}</b>. </>}
          {men.pan_redigidos > 0 && <>Cartões escritos na conversa e apagados: <b>{men.pan_redigidos}</b>.</>}
        </Alert>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab icon={<QueryStats />} iconPosition="start" label="Por loja e por dia" />
          <Tab icon={<Forum />} iconPosition="start" label={`Conversas (${sessoes.length})`} />
          <Tab icon={<Storefront />} iconPosition="start" label={`Comerciantes (${comerciantes.filter((c) => c.activo).length} no canal)`} />
          <Tab icon={<Bolt />} iconPosition="start" label={`Serviços Pagali (${servicos.filter((s) => s.activo).length})`} />
        </Tabs>
      </Paper>

      {loading && !painel ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box> : null}

      {tab === 0 && painel && (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Por loja</Typography>
              <TableContainer><Table size="small">
                <TableHead><TableRow><TableCell>Loja</TableCell><TableCell align="right">Conversas</TableCell><TableCell align="right">Pedidos</TableCell><TableCell align="right">Pagos</TableCell><TableCell align="right">Receita</TableCell></TableRow></TableHead>
                <TableBody>
                  {painel.por_loja.length === 0 && <TableRow><TableCell colSpan={5}><Typography color="text.secondary">Sem pedidos no período.</Typography></TableCell></TableRow>}
                  {painel.por_loja.map((l) => <TableRow key={l.loja_id}><TableCell>{l.loja}</TableCell><TableCell align="right">{l.sessoes}</TableCell><TableCell align="right">{l.carrinhos}</TableCell><TableCell align="right">{l.pagos}</TableCell><TableCell align="right">{cve(l.receita_cve)}</TableCell></TableRow>)}
                </TableBody>
              </Table></TableContainer>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Por dia</Typography>
              <TableContainer><Table size="small">
                <TableHead><TableRow><TableCell>Dia</TableCell><TableCell align="right">Conversas</TableCell><TableCell align="right">Pagos</TableCell><TableCell align="right">Receita</TableCell></TableRow></TableHead>
                <TableBody>
                  {painel.por_dia.length === 0 && <TableRow><TableCell colSpan={4}><Typography color="text.secondary">Sem conversas no período.</Typography></TableCell></TableRow>}
                  {painel.por_dia.map((d) => <TableRow key={String(d.dia)}><TableCell>{String(d.dia).slice(0, 10)}</TableCell><TableCell align="right">{d.sessoes}</TableCell><TableCell align="right">{d.pagos}</TableCell><TableCell align="right">{cve(d.receita_cve)}</TableCell></TableRow>)}
                </TableBody>
              </Table></TableContainer>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Pedidos e mensagens</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {car && Object.entries({ abertos: car.abertos, confirmados: car.confirmados, 'com link': car.com_link, pagos: car.pagos, abandonados: car.abandonados, cancelados: car.cancelados }).map(([k, v]) => <Chip key={k} label={`${k}: ${v}`} variant="outlined" />)}
                {men && <Chip label={`mensagens: ${men.recebidas} recebidas / ${men.enviadas} enviadas`} variant="outlined" />}
                {men && <Chip label={`intenções: ${men.intencoes_lyra} Lyra / ${men.intencoes_regras} regras`} variant="outlined" />}
                {ses && <Chip label={`em atendimento humano: ${ses.em_humano}`} variant="outlined" color={ses.em_humano ? 'error' : 'default'} />}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tab === 1 && (
        <DataTable columns={colunasSessoes} data={sessoes} loading={loading} rowKey="id" onView={abrirSessao} emptyMessage="Ainda não há conversas." />
      )}

      {tab === 2 && (
        <Paper>
          <TableContainer><Table>
            <TableHead><TableRow><TableCell>Loja</TableCell><TableCell align="right">Produtos activos</TableCell><TableCell>Quem recebe</TableCell><TableCell align="right">Limite por conversa</TableCell><TableCell>Número</TableCell><TableCell>Link WhatsApp</TableCell><TableCell>No canal</TableCell><TableCell /></TableRow></TableHead>
            <TableBody>
              {comerciantes.map((c) => (
                <TableRow key={c.loja_id} hover>
                  <TableCell><b>{c.nome}</b>{c.slug && <Typography variant="caption" color="text.secondary" display="block">{c.slug}</Typography>}</TableCell>
                  <TableCell align="right">{c.produtos_activos}</TableCell>
                  <TableCell>{c.recebe_email || <Typography variant="caption" color="text.secondary">por definir</Typography>}</TableCell>
                  <TableCell align="right">{c.limite_sessao_cve != null ? cve(c.limite_sessao_cve) : '—'}</TableCell>
                  <TableCell>{c.numero_whatsapp ? <><b>+{c.numero_whatsapp}</b>{c.instancia && <Typography variant="caption" color="text.secondary" display="block">{c.instancia}</Typography>}<Button size="small" onClick={() => verInstancia(c.loja_id, c.nome)} disabled={qrOcupado}>Ligação</Button></> : <Typography variant="caption" color="text.secondary">número da Pagali</Typography>}</TableCell>
                  <TableCell>{c.link_whatsapp ? <Tooltip title={c.link_whatsapp}><Button size="small" startIcon={<ContentCopy />} onClick={() => { navigator.clipboard.writeText(c.link_whatsapp!); setMsg('Link copiado'); }}>Copiar</Button></Tooltip> : <Typography variant="caption" color="text.secondary">sem slug</Typography>}</TableCell>
                  <TableCell>
                    <Tooltip title={c.no_canal ? (c.activo ? 'Aberta no Chat-to-Pay' : 'Fechada') : 'Ainda não está no canal: clica para configurar'}>
                      <Switch checked={c.no_canal && c.activo} onChange={() => alternarActivo(c)} color="success" />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right"><Button size="small" onClick={() => abrirComerciante(c)}>Configurar</Button></TableCell>
                </TableRow>
              ))}
              {comerciantes.length === 0 && <TableRow><TableCell colSpan={8}><Typography color="text.secondary">Não há lojas activas.</Typography></TableCell></TableRow>}
            </TableBody>
          </Table></TableContainer>
        </Paper>
      )}

      {/* Conversa: eventos */}
      <Dialog open={!!sessao} onClose={() => setSessao(null)} maxWidth="md" fullWidth>
        <DialogTitle>Conversa {sessao?.id.slice(0, 8)} · {sessao?.loja || 'sem loja'} · <EstadoChip estado={sessao?.estado || null} /></DialogTitle>
        <DialogContent dividers>
          {eventos.length === 0 && <Typography color="text.secondary">Sem eventos.</Typography>}
          <Stack spacing={1}>
            {eventos.map((e) => (
              <Box key={e.id} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', opacity: e.direcao === 'SYS' ? 0.75 : 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 64, pt: 0.5, fontVariantNumeric: 'tabular-nums' }}>{new Date(e.created_at).toLocaleTimeString('pt-PT', { timeZone: 'Atlantic/Cape_Verde', hour: '2-digit', minute: '2-digit', second: '2-digit' })}</Typography>
                <Box sx={{ flex: 1, p: 1, borderRadius: 1, bgcolor: e.direcao === 'IN' ? 'action.hover' : e.direcao === 'OUT' ? 'primary.main' : 'transparent', color: e.direcao === 'OUT' ? 'primary.contrastText' : 'text.primary', border: e.direcao === 'SYS' ? '1px dashed' : 'none', borderColor: 'divider' }}>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>{e.tipo}{e.estado_depois && e.estado_depois !== e.estado_antes ? ` · ${e.estado_antes} → ${e.estado_depois}` : ''}</Typography>
                  {e.conteudo && <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{e.conteudo}</Typography>}
                  {!e.conteudo && e.dados && <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>{JSON.stringify(e.dados)}</Typography>}
                  {e.erro && <Typography variant="caption" color="error.main" display="block">{e.erro}</Typography>}
                </Box>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          {sessao && sessao.estado !== 'ENCERRADA' && <Button color="warning" onClick={async () => { await chatToPayService.encerrar(sessao.id); setMsg('Conversa encerrada'); setSessao(null); carregar(); }}>Encerrar conversa</Button>}
          <Button onClick={() => setSessao(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Comerciante */}
      <Dialog open={!!comerciante} onClose={() => setComerciante(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{comerciante?.nome} no Chat-to-Pay</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Quem recebe (email do utilizador Pagali com carteira)" value={form.recebe_email} onChange={(e) => setForm({ ...form, recebe_email: e.target.value })} helperText="A carteira deste utilizador é creditada pelo líquido de cada pedido pago." fullWidth />
            <TextField label="Saudação" value={form.saudacao} onChange={(e) => setForm({ ...form, saudacao: e.target.value })} multiline minRows={2} fullWidth helperText="A primeira mensagem que o cliente recebe, antes da ementa." />
            <TextField label="Limite por conversa (CVE)" type="number" value={form.limite_sessao_cve} onChange={(e) => setForm({ ...form, limite_sessao_cve: e.target.value })} helperText="Pedidos acima disto são recusados na conversa. Vazio = sem limite." fullWidth />
            <TextField label="Número de WhatsApp da loja" value={form.numero_whatsapp} onChange={(e) => setForm({ ...form, numero_whatsapp: e.target.value })} helperText="Com número próprio, quem escreve a este número fala logo com esta loja. Vazio = atrás do número da Pagali, com escolha por lista." fullWidth />
            <TextField label="Instância (Evolution) que atende esse número" value={form.instancia} onChange={(e) => setForm({ ...form, instancia: e.target.value })} helperText="Preenchido automaticamente ao gravar o número: a Pagali cria a instância e mostra o QR para o restaurante ler. Só mudar se a instância já existir com outro nome." fullWidth />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Switch checked={form.activo} onChange={(e) => setForm({ ...form, activo: e.target.checked })} color="success" /><Typography>Aberta no canal</Typography></Box>
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setComerciante(null)}>Cancelar</Button><Button variant="contained" onClick={guardarComerciante}>Guardar</Button></DialogActions>
      </Dialog>

      {tab === 3 && (
        <Paper>
          <Box sx={{ p: 2, pb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Os serviços que a <b>Pagali</b> cobra na conversa, no seu próprio número. O dinheiro vai para a entidade pela SISP —
              não passa por carteira nenhuma. Uma referência errada paga a conta de outra pessoa, por isso a conversa faz sempre confirmar o número.
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={abrirNovo} sx={{ flexShrink: 0 }}>Adicionar</Button>
          </Box>
          <TableContainer><Table>
            <TableHead><TableRow>
              <TableCell>Serviço</TableCell><TableCell>Entidade SISP</TableCell><TableCell>O que se pede</TableCell>
              <TableCell align="right">Valores</TableCell><TableCell align="right">Pagos</TableCell><TableCell align="right">Receita</TableCell>
              <TableCell>Aberto</TableCell><TableCell />
            </TableRow></TableHead>
            <TableBody>
              {servicos.map((sv) => (
                <TableRow key={sv.id} hover>
                  <TableCell><b>{sv.rotulo}</b>{sv.devolve_token && <Chip size="small" label="devolve código" color="secondary" variant="outlined" sx={{ ml: 1 }} />}</TableCell>
                  <TableCell>{sv.entidade_nome}<Typography variant="caption" color="text.secondary" display="block">código {sv.entidade_sisp}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{sv.transaction_code === '3' ? 'número de 6 ou 7 dígitos' : 'referência de 9 dígitos'}</Typography></TableCell>
                  <TableCell align="right">{sv.valor_min != null || sv.valor_max != null ? `${cve(sv.valor_min)} – ${cve(sv.valor_max)}` : 'sem limite'}</TableCell>
                  <TableCell align="right">{sv.pagos}</TableCell>
                  <TableCell align="right">{cve(sv.receita_cve)}</TableCell>
                  <TableCell><Switch checked={sv.activo} onChange={() => alternarServico(sv)} color="success" /></TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => abrirServico(sv)}>Configurar</Button>
                    {sv.pagos === 0 && <Tooltip title="Remover"><IconButton size="small" color="error" onClick={() => removerServico(sv)}><DeleteOutline fontSize="small" /></IconButton></Tooltip>}
                  </TableCell>
                </TableRow>
              ))}
              {servicos.length === 0 && <TableRow><TableCell colSpan={8}><Typography color="text.secondary">Ainda não há serviços. Corre a migração <code>chat-to-pay-servicos</code>.</Typography></TableCell></TableRow>}
            </TableBody>
          </Table></TableContainer>
        </Paper>
      )}

      {/* Serviço: adicionar a partir das entidades da SISP */}
      <Dialog open={novoAberto} onClose={() => setNovoAberto(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar serviço<Typography variant="caption" color="text.secondary" display="block">As entidades são as do menu SISP. É o código de cada uma que decide a quem a SISP entrega o dinheiro.</Typography></DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label="Entidade" value={formN.entidade_id} onChange={(e) => setFormN({ ...formN, entidade_id: e.target.value })} fullWidth>
              {entidades.map((en) => (
                <MenuItem key={en.id} value={en.id} disabled={formN.tipo_servico === 'RCG' ? en.tem_recarga : en.tem_factura}>
                  {en.nome} — código {en.codigo}{en.tipo ? ` · ${en.tipo}` : ''}{(formN.tipo_servico === 'RCG' ? en.tem_recarga : en.tem_factura) ? ' (já configurado)' : ''}
                </MenuItem>
              ))}
            </TextField>
            <TextField select label="Modo" value={formN.tipo_servico} onChange={(e) => setFormN({ ...formN, tipo_servico: e.target.value as 'RCG' | 'FAT' })} fullWidth
              helperText={formN.tipo_servico === 'RCG' ? 'Recarga: pede um número de 6 ou 7 dígitos (contador ou telemóvel).' : 'Factura: pede a referência de pagamento, 9 dígitos.'}>
              <MenuItem value="RCG">Recarga (contador ou telemóvel)</MenuItem>
              <MenuItem value="FAT">Factura</MenuItem>
            </TextField>
            <TextField label="Como aparece na conversa" value={formN.rotulo} onChange={(e) => setFormN({ ...formN, rotulo: e.target.value })} fullWidth placeholder="Electra Norte — carregar contador" helperText="Deixa vazio para um nome automático. É por estas palavras que o cliente escolhe." />
            {formN.tipo_servico === 'RCG' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Switch checked={formN.devolve_token} onChange={(e) => setFormN({ ...formN, devolve_token: e.target.checked })} />
                <Typography variant="body2">Devolve código para digitar no contador (Electra pré-pago)</Typography>
              </Box>
            )}
            <Alert severity="info">O serviço nasce <b>fechado</b>. Confere o rótulo e a frase que pede a referência, e só depois abre.</Alert>
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setNovoAberto(false)}>Cancelar</Button><Button variant="contained" onClick={criarServico} disabled={!formN.entidade_id}>Criar</Button></DialogActions>
      </Dialog>

      {/* Serviço: configurar */}
      <Dialog open={!!servico} onClose={() => setServico(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{servico?.entidade_nome} <Typography variant="caption" color="text.secondary" display="block">código SISP {servico?.entidade_sisp} · {servico?.transaction_code === '3' ? 'recarga' : 'factura'}</Typography></DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Como aparece na conversa" value={formS.rotulo} onChange={(e) => setFormS({ ...formS, rotulo: e.target.value })} fullWidth helperText="É por estas palavras que o cliente escolhe, escrevendo o nome ou o número da lista." />
            <TextField label="O que pedir ao cliente" value={formS.ajuda_referencia} onChange={(e) => setFormS({ ...formS, ajuda_referencia: e.target.value })} multiline minRows={2} fullWidth helperText="A frase que pede a referência. Diz onde ela está e quantos dígitos tem." />
            <Stack direction="row" spacing={2}>
              <TextField label="Valor mínimo (CVE)" type="number" value={formS.valor_min} onChange={(e) => setFormS({ ...formS, valor_min: e.target.value })} fullWidth />
              <TextField label="Valor máximo (CVE)" type="number" value={formS.valor_max} onChange={(e) => setFormS({ ...formS, valor_max: e.target.value })} fullWidth />
            </Stack>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Switch checked={formS.activo} onChange={(e) => setFormS({ ...formS, activo: e.target.checked })} color="success" /><Typography>Aberto na conversa</Typography></Box>
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setServico(null)}>Cancelar</Button><Button variant="contained" onClick={guardarServico}>Guardar</Button></DialogActions>
      </Dialog>

      {/* Instância de WhatsApp da loja: QR e estado */}
      <Dialog open={!!qrLoja} onClose={() => setQrLoja(null)} maxWidth="xs" fullWidth>
        <DialogTitle>WhatsApp da {qrLoja?.nome}</DialogTitle>
        <DialogContent dividers>
          {qrLoja?.estado === 'sem_instancia' && <Typography>Esta loja ainda não tem instância. Grava o número da loja em «Configurar» e a instância é criada.</Typography>}
          {qrLoja?.estado === 'open' && <Alert severity="success">Ligado. O número +{qrLoja.numero} responde por <b>{qrLoja.instancia}</b>.</Alert>}
          {qrLoja && !['open', 'sem_instancia'].includes(qrLoja.estado) && (
            <Stack spacing={1.5} alignItems="center">
              {qrLoja.qr ? <img src={qrLoja.qr} alt="QR para ligar o WhatsApp" style={{ width: 260, height: 260 }} /> : <CircularProgress />}
              <Typography variant="body2" align="center">No telemóvel da loja: WhatsApp → Dispositivos ligados → Ligar dispositivo → ler este código. A página verifica sozinha a cada 5 segundos.</Typography>
              <Chip size="small" label={`estado: ${qrLoja.estado}`} color={qrLoja.estado === 'connecting' ? 'warning' : 'default'} />
              {qrLoja.erro && <Alert severity="error">{qrLoja.erro}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {qrLoja?.instancia && <Button color="error" onClick={async () => { if (!window.confirm('Apagar a instância na Evolution? O número deixa de estar ligado.')) return; try { await chatToPayService.instanciaApagar(qrLoja.lojaId); setMsg('Instância apagada'); setQrLoja(null); carregar(); } catch (e: any) { setErro(e?.response?.data?.error || 'Não foi possível apagar'); } }}>Apagar instância</Button>}
          {qrLoja?.estado === 'open' && <Button onClick={async () => { try { await chatToPayService.instanciaWebhook(qrLoja.lojaId, true); setMsg('Mensagens ligadas ao Chat-to-Pay'); } catch (e: any) { setErro(e?.response?.data?.error || 'Não foi possível ligar'); } }}>Ligar mensagens</Button>}
          <Button onClick={() => setQrLoja(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Simulador */}
      <Dialog open={simAberto} onClose={() => setSimAberto(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Simular uma conversa <Typography variant="caption" color="text.secondary" display="block">Canal SIMULADOR · nada é enviado ao WhatsApp · contacto fictício {simContacto}</Typography></DialogTitle>
        <DialogContent dividers sx={{ minHeight: 320 }}>
          <Stack spacing={1}>
            {simLinhas.length === 0 && <Typography color="text.secondary">Escreve como o cliente: «olá», «quero 2 cachupas ricas», «confirmar», «sim»…</Typography>}
            {simLinhas.map((l, i) => (
              <Box key={i} sx={{ alignSelf: l.de === 'eu' ? 'flex-end' : 'flex-start', maxWidth: '85%', p: 1, borderRadius: 1, bgcolor: l.de === 'eu' ? 'primary.main' : l.de === 'lyra' ? 'action.hover' : 'transparent', color: l.de === 'eu' ? 'primary.contrastText' : 'text.primary', fontSize: l.de === 'nota' ? 12 : 14, opacity: l.de === 'nota' ? 0.7 : 1, whiteSpace: 'pre-wrap' }}>{l.texto}</Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ gap: 1 }}>
          <TextField fullWidth size="small" placeholder="Mensagem do cliente" value={simTexto} onChange={(e) => setSimTexto(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') simularEnviar(); }} disabled={simOcupado} />
          <Button variant="contained" endIcon={<Send />} onClick={simularEnviar} disabled={simOcupado || !simTexto.trim()}>Enviar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!msg} autoHideDuration={3000} onClose={() => setMsg('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}><Alert severity="success">{msg}</Alert></Snackbar>
    </Box>
  );
};

export default ChatToPay;
